import { MapNavigationControl } from '../lib/map/navigationControl';
import { screenVehicleHeading } from '../lib/map/vehicleHeading';
import { attachBodycamLayer } from '../features/bodycams/mapLayer';
import type { BodycamPoint } from '../features/bodycams/useBodycams';
import type { TrailPoint } from '../../convex/lib/bodycamData';
import { attachFrontierTownModel } from '../lib/map/frontierTownModel';
import { siteMarkerPosition, type MarkerEditDraft } from '../utils/siteMarkerPosition';
import { focusSiteModel, modeledSiteGeofenceFilter, type SiteModelFocusRequest } from '../lib/map/siteModelFocus';
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import mapboxgl from '../lib/mapboxClient';
import { attachRenderQuality } from '../lib/map/renderQuality';
import { attachSiteMarkerLayer, isSiteMarkerHit } from '../lib/map/siteMarkerLayer';
import { attachFifteenthStreetModel } from '../lib/map/fifteenthStreetModel';
import { attachCookMarketplaceModel } from '../lib/map/cookMarketplaceModel';
import { attachApsHqModel } from '../lib/map/apsHqModel';
import { sampleVehicleHistory } from '../lib/map/vehicleInterpolation';
import type { MapLightPreset } from '../lib/map/customModelLighting';
import 'mapbox-gl/dist/mapbox-gl.css';
import { normalizeLatLng, normalizeLngLatTuple } from '../utils/geo';
import { normalizeSiteTypeLabel } from '../utils/siteTypes';

// Constants from original script
const REFRESH_INTERVAL = 3000;
const DISPLAY_OFFSET = REFRESH_INTERVAL * 2;
const MAX_HISTORY = 10;
const MARKER_POSITION_EPSILON = 1e-8;

type MapStyleMode = 'standard' | 'satellite';
type MapLayerSelection = MapLightPreset | 'satellite';
type MapLayerOption = { id: MapLayerSelection; label: string; thumbnailUrl?: string | null };
type LayerThumbnails = Partial<Record<MapLayerSelection, string | null>>;
type VehicleMarkerDomRef = {
  wrapper: HTMLElement;
  label: HTMLElement;
  icon: HTMLElement;
  heading: number;
};
type BasemapStandardSettingKey =
  | 'showPointOfInterestLabels'
  | 'showTransitLabels'
  | 'showLandmarkIcons'
  | 'showLandmarkIconLabels'
  | 'show3dObjects'
  | 'show3dBuildings'
  | 'show3dLandmarks'
  | 'show3dFacades'
  | 'show3dTrees';
type BasemapSatelliteSettingKey =
  | 'showPointOfInterestLabels'
  | 'showTransitLabels'
  | 'showLandmarkIcons'
  | 'showLandmarkIconLabels';

const BASEMAP_STANDARD_SETTINGS: Record<BasemapStandardSettingKey, boolean> = {
  showPointOfInterestLabels: false,
  showTransitLabels: false,
  showLandmarkIcons: false,
  showLandmarkIconLabels: false,
  show3dObjects: true,
  show3dBuildings: true,
  show3dLandmarks: true,
  // Decorative basemap geometry competes with site models during zoom.
  show3dFacades: false,
  show3dTrees: false,
};

const BASEMAP_SATELLITE_SETTINGS: Record<BasemapSatelliteSettingKey, boolean> = {
  showPointOfInterestLabels: false,
  showTransitLabels: false,
  showLandmarkIcons: false,
  showLandmarkIconLabels: false,
};

const BASE_MAP_LAYER_OPTIONS: Array<{ id: MapLayerSelection; label: string }> = [
  { id: 'dawn', label: 'Dawn' },
  { id: 'day', label: 'Day' },
  { id: 'dusk', label: 'Dusk' },
  { id: 'night', label: 'Night' },
  { id: 'satellite', label: 'Satellite' },
];

const LAYER_THUMB_CACHE_PREFIX = 'layer-thumb-cache-v1:';
const LAYER_THUMB_CACHE_WIDTH = 176;
const LAYER_THUMB_CACHE_HEIGHT = 120;

function getLayerThumbCacheKey(selection: MapLayerSelection, sourceUrl: string) {
  return `${LAYER_THUMB_CACHE_PREFIX}${selection}:${sourceUrl}`;
}

function readCachedLayerThumb(selection: MapLayerSelection, sourceUrl: string): string | null {
  try {
    return localStorage.getItem(getLayerThumbCacheKey(selection, sourceUrl));
  } catch {
    return null;
  }
}

function saveCachedLayerThumb(selection: MapLayerSelection, sourceUrl: string, dataUrl: string) {
  try {
    localStorage.setItem(getLayerThumbCacheKey(selection, sourceUrl), dataUrl);
  } catch {
    // Ignore storage quota / private mode failures.
  }
}

async function compressLayerThumbUrl(sourceUrl: string): Promise<string | null> {
  try {
    const response = await fetch(sourceUrl, { cache: 'force-cache', mode: 'cors' });
    if (!response.ok) return null;
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Layer thumbnail decode failed'));
        image.src = objectUrl;
      });

      const canvas = document.createElement('canvas');
      canvas.width = LAYER_THUMB_CACHE_WIDTH;
      canvas.height = LAYER_THUMB_CACHE_HEIGHT;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      const scale = Math.max(
        LAYER_THUMB_CACHE_WIDTH / img.naturalWidth,
        LAYER_THUMB_CACHE_HEIGHT / img.naturalHeight
      );
      const drawWidth = img.naturalWidth * scale;
      const drawHeight = img.naturalHeight * scale;
      const dx = (LAYER_THUMB_CACHE_WIDTH - drawWidth) / 2;
      const dy = (LAYER_THUMB_CACHE_HEIGHT - drawHeight) / 2;
      ctx.drawImage(img, dx, dy, drawWidth, drawHeight);

      return canvas.toDataURL('image/webp', 0.72);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch {
    return null;
  }
}

class StyleDropdownControl {
  private container: HTMLDivElement | null = null;
  private triggerButton: HTMLButtonElement | null = null;
  private menu: HTMLDivElement | null = null;
  private optionButtons = new globalThis.Map<MapLayerSelection, HTMLButtonElement>();
  private optionThumbs = new globalThis.Map<MapLayerSelection, HTMLDivElement>();
  private selectedOption: MapLayerSelection;
  private options: MapLayerOption[];
  private isOpen = false;
  private outsideClickHandler: ((event: MouseEvent) => void) | null = null;

  constructor(
    private readonly onSelect: (selection: MapLayerSelection) => void,
    defaultSelection: MapLayerSelection,
    options: MapLayerOption[]
  ) {
    this.selectedOption = defaultSelection;
    this.options = options;
  }

  onAdd(_mapInstance: mapboxgl.Map) {
    this.container = document.createElement('div');
    this.container.className = 'style-dropdown-control mapboxgl-ctrl';

    this.triggerButton = document.createElement('button');
    this.triggerButton.type = 'button';
    this.triggerButton.className = 'style-dropdown-trigger';
    this.triggerButton.setAttribute('aria-label', 'Map layers');
    this.triggerButton.onclick = (event) => {
      event.stopPropagation();
      this.toggleMenu();
    };

    const triggerIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    triggerIcon.setAttribute('viewBox', '0 0 24 24');
    triggerIcon.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    triggerIcon.setAttribute('aria-hidden', 'true');
    triggerIcon.classList.add('style-dropdown-trigger-icon');
    const iconPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    iconPath.setAttribute(
      'd',
      'M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74zM12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z'
    );
    triggerIcon.appendChild(iconPath);
    this.triggerButton.appendChild(triggerIcon);
    this.container.appendChild(this.triggerButton);

    this.menu = document.createElement('div');
    this.menu.className = 'style-dropdown-menu';
    this.menu.setAttribute('role', 'menu');

    const menuHeader = document.createElement('div');
    menuHeader.className = 'style-dropdown-menu-header';
    menuHeader.textContent = 'Map Style';
    this.menu.appendChild(menuHeader);

    this.container.appendChild(this.menu);

    this.renderOptions();

    this.outsideClickHandler = (event: MouseEvent) => {
      if (!this.container) return;
      if (!(event.target instanceof Node)) return;
      if (this.container.contains(event.target)) return;
      this.closeMenu();
    };
    document.addEventListener('click', this.outsideClickHandler);

    return this.container;
  }

  onRemove() {
    if (this.outsideClickHandler) {
      document.removeEventListener('click', this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
    if (this.container?.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this.container = null;
    this.triggerButton = null;
    this.menu = null;
    this.optionButtons.clear();
    this.optionThumbs.clear();
    this.isOpen = false;
  }

  setOptions(options: MapLayerOption[]) {
    this.options = options;
    if (!this.menu) return;
    this.renderOptions();
  }

  syncSelection(selection: MapLayerSelection) {
    this.selectedOption = selection;
    const label =
      this.options.find((option) => option.id === selection)?.label ?? 'Layers';

    if (this.triggerButton) {
      this.triggerButton.setAttribute('title', `Layer: ${label}`);
      this.triggerButton.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');0
    }

    this.optionButtons.forEach((button, optionId) => {
      const isActive = optionId === selection;
      button.className = isActive
        ? 'style-dropdown-option active'
        : 'style-dropdown-option';
      button.setAttribute('aria-checked', isActive ? 'true' : 'false');
    });
  }

  private renderOptions() {
    if (!this.menu) return;
    // Preserve the header, rebuild only the options grid
    const existingGrid = this.menu.querySelector('.style-dropdown-options-grid');
    if (existingGrid) this.menu.removeChild(existingGrid);
    this.optionButtons.clear();
    this.optionThumbs.clear();

    const grid = document.createElement('div');
    grid.className = 'style-dropdown-options-grid';

    this.options.forEach((option) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'style-dropdown-option';
      item.setAttribute('role', 'menuitemradio');
      item.onclick = (event) => {
        event.stopPropagation();
        this.onSelect(option.id);
        this.syncSelection(option.id);
        this.closeMenu();
      };

      const thumb = document.createElement('div');
      thumb.className = 'style-dropdown-option-thumb';
      if (option.thumbnailUrl) {
        const thumbImg = document.createElement('img');
        thumbImg.className = 'style-dropdown-option-thumb-img';
        thumbImg.src = option.thumbnailUrl;
        thumbImg.alt = `${option.label} preview`;
        thumbImg.width = 56;
        thumbImg.height = 38;
        thumbImg.loading = 'eager';
        thumbImg.setAttribute('decoding', 'async');
        thumbImg.setAttribute('fetchpriority', 'high');
        thumb.appendChild(thumbImg);
        thumb.classList.add('has-image');
      } else {
        thumb.classList.add('no-image');
      }

      const label = document.createElement('span');
      label.className = 'style-dropdown-option-label';
      label.textContent = option.label;

      item.appendChild(thumb);
      item.appendChild(label);

      grid.appendChild(item);
      this.optionButtons.set(option.id, item);
      this.optionThumbs.set(option.id, thumb);
    });

    this.menu.appendChild(grid);
    this.syncSelection(this.selectedOption);
  }

  private toggleMenu() {
    if (this.isOpen) {
      this.closeMenu();
      return;
    }
    this.openMenu();
  }

  private openMenu() {
    if (!this.menu) return;
    this.isOpen = true;
    this.menu.classList.add('open');
    this.triggerButton?.setAttribute('aria-expanded', 'true');
  }

  private closeMenu() {
    if (!this.menu) return;
    this.isOpen = false;
    this.menu.classList.remove('open');
    this.triggerButton?.setAttribute('aria-expanded', 'false');
  }
}

function normalizeHeadingDegrees(raw: unknown): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return 0;
  return ((Math.round(raw) % 360) + 360) % 360;
}


interface MapProps {
  bodycamPoints?: BodycamPoint[];
  bodycamTrail?: TrailPoint[];
  selectedBodycamId?: string | null;
  bodycamFocusRequest?: number;
  onSelectBodycam?: (id: string) => void;
  vehicles: any[];
  sites: any[];
  showSiteMarkers?: boolean;
  markerEditDraft?: MarkerEditDraft | null;
  markerEditSaving?: boolean;
  onMarkerEditChange?: (coordinates: [number, number]) => void;
  siteMarkerSizePx?: number;
  siteMarkerSizePxByType?: Record<string, number>;
  selectedVehicleId: string | null;
  selectedSiteId: string | null;
  siteModelFocusRequest?: SiteModelFocusRequest | null;
  dispatchResult: any | null;
  rspndrDestination?: { lat: number; lng: number; label?: string } | null;
  showGeofences: boolean;
  geofenceEditSiteId?: string | null;
  onGeofenceEditChange?: (ring: [number, number][]) => void;
  geofenceEditorEnabled?: boolean;
  onGeofencePickForEdit?: (siteId: string) => void;
  mapStyleMode?: MapStyleMode;
  onMapStyleModeChange: (mode: MapStyleMode) => void;
  panToSelection?: boolean;
  onSelectVehicle: (id: string) => void;
  onSelectSite: (id: string) => void;
  assetUrls?: {
    Vehicle_IsMoving: string | null;
    Vehicle_NotMoving: string | null;
    SiteIcon: string | null;
    SiteIconsByType?: Record<string, string | null>;
  };
  layerThumbnails?: {
    dawn?: string | null;
    day?: string | null;
    dusk?: string | null;
    night?: string | null;
    satellite?: string | null;
  };
}

// Fallback local paths (used only if assets not uploaded to DB)
const FALLBACK_ASSETS = {
  Vehicle_IsMoving: '/WebAssets/Vehicle_IsMoving.png',
  Vehicle_NotMoving: '/WebAssets/Vehicle_NotMoving.png',
  SiteIcon: '/WebAssets/SiteIcon.png',
};

const Map: React.FC<MapProps> = ({ 
  vehicles, 
  bodycamPoints, bodycamTrail, selectedBodycamId, bodycamFocusRequest, onSelectBodycam,
  sites,
  showSiteMarkers = true,
  markerEditDraft, markerEditSaving = false, onMarkerEditChange,
  siteMarkerSizePx = 24,
  siteMarkerSizePxByType,
  selectedVehicleId,
  selectedSiteId,
  siteModelFocusRequest,
  dispatchResult,
  rspndrDestination,
  showGeofences,
  geofenceEditSiteId,
  onGeofenceEditChange,
  geofenceEditorEnabled = false,
  onGeofencePickForEdit,
  mapStyleMode = 'standard',
  onMapStyleModeChange,
  panToSelection = false,
  onSelectVehicle,
  onSelectSite,
  assetUrls,
  layerThumbnails
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const focusedBodycamRef = useRef<string | null>(null);
  const bodycamLayerRef = useRef<ReturnType<typeof attachBodycamLayer> | null>(null);
  const bodycamSelectRef = useRef(onSelectBodycam);
  bodycamSelectRef.current = onSelectBodycam;
  const hqModelRef = useRef<ReturnType<typeof attachApsHqModel> | null>(null);
  const cookModelRef = useRef<ReturnType<typeof attachCookMarketplaceModel> | null>(null);
  const frontierModelRef = useRef<ReturnType<typeof attachFrontierTownModel> | null>(null);
  const [frontierModelError, setFrontierModelError] = useState<string | null>(null);
  const fifteenthModelRef = useRef<ReturnType<typeof attachFifteenthStreetModel> | null>(null);
  const [fifteenthModelError, setFifteenthModelError] = useState<string | null>(null);
  const [cookModelError, setCookModelError] = useState<string | null>(null);
  const [hqModelError, setHqModelError] = useState<string | null>(null);
  const vehicleMarkersRef = useRef<{ [key: string]: mapboxgl.Marker }>({});
  const vehicleMarkerCreatingRef = useRef<{ [key: string]: boolean }>({});
  const siteMarkerLayerRef = useRef<ReturnType<typeof attachSiteMarkerLayer> | null>(null);
  const siteSelectRef = useRef({ onSelectSite, onGeofencePickForEdit });
  siteSelectRef.current = { onSelectSite, onGeofencePickForEdit };
  const rspndrMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const vehicleHistoriesRef = useRef<{ [key: string]: { time: number; coord: [number, number] }[] }>({});
  const animationFrameRef = useRef<number>();
  const animationTimeoutRef = useRef<number | null>(null);
  const vehicleLastAnimatedCoordsRef = useRef<Record<string, [number, number]>>({});
  const vehicleMarkerDomRefsRef = useRef<Record<string, VehicleMarkerDomRef>>({});
  const routeSourceRef = useRef<string | null>(null);
  const geofenceEditorEnabledRef = useRef(false);
  const geofenceClickHandlersRef = useRef(new globalThis.Map<string, any>());
  const shouldFitRouteRef = useRef(false);
  const allGeofencesSourceId = 'all-geofences-combined';
  const allGeofencesLayerId = 'all-geofences-layer';
  const allGeofencesBorderLayerId = 'all-geofences-border-layer';
  const regularGeofenceSlot = 'middle' as const;
  const editGeofenceSlot = 'top' as const;
  const routeWasDrawnRef = useRef(false);
  const lastStyleModeRef = useRef<MapStyleMode>('standard');
  const styleDropdownControlRef = useRef<StyleDropdownControl | null>(null);
  const lastRenderedGeofenceSignatureRef = useRef<string>('');
  const geofenceParseCacheRef = useRef(new globalThis.Map<string, number[][][] | null>());
  const [lightPreset, setLightPreset] = useState<MapLightPreset>('dusk');
  const sitesByIdRef = useRef<Record<string, any>>({});
  const [resolvedLayerThumbnails, setResolvedLayerThumbnails] = useState<LayerThumbnails>({});

  const getSiteMarkerIconUrl = useCallback(
    (siteType?: string | null) => {
      const byType = siteType ? assetUrls?.SiteIconsByType?.[siteType] : null;
      return byType || assetUrls?.SiteIcon || FALLBACK_ASSETS.SiteIcon;
    },
    [assetUrls]
  );

  const getSiteMarkerSizePx = useCallback(
    (siteType?: string | null) => {
      const byType = siteType ? siteMarkerSizePxByType?.[siteType] : null;
      if (typeof byType === 'number' && Number.isFinite(byType)) return byType;
      return siteMarkerSizePx;
    },
    [siteMarkerSizePx, siteMarkerSizePxByType]
  );

  const getSiteMarkerZIndex = useCallback(
    (siteId: string, siteType?: string | null) => {
      const normalizedType = normalizeSiteTypeLabel(siteType);
      const baseZIndex = normalizedType === 'HQ' ? 15 : 10;
      if (siteId === selectedSiteId) {
        return normalizedType === 'HQ' ? '21' : '20';
      }
      return String(baseZIndex);
    },
    [selectedSiteId]
  );

  useEffect(() => {
    const baseResolved: LayerThumbnails = {};
    const toCompress: Array<{ id: MapLayerSelection; url: string }> = [];

    BASE_MAP_LAYER_OPTIONS.forEach((option) => {
      const url = layerThumbnails?.[option.id];
      if (!url) {
        baseResolved[option.id] = null;
        return;
      }

      const cached = readCachedLayerThumb(option.id, url);
      if (cached) {
        baseResolved[option.id] = cached;
        return;
      }

      baseResolved[option.id] = url;
      toCompress.push({ id: option.id, url });
    });

    setResolvedLayerThumbnails(baseResolved);

    let cancelled = false;
    (async () => {
      for (const pending of toCompress) {
        const compressed = await compressLayerThumbUrl(pending.url);
        if (!compressed || cancelled) continue;
        saveCachedLayerThumb(pending.id, pending.url, compressed);
        setResolvedLayerThumbnails((prev) => {
          if ((layerThumbnails?.[pending.id] || null) !== pending.url) return prev;
          return { ...prev, [pending.id]: compressed };
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [layerThumbnails]);

  const layerOptions = useMemo<MapLayerOption[]>(
    () =>
      BASE_MAP_LAYER_OPTIONS.map((option) => ({
        id: option.id,
        label: option.label,
        thumbnailUrl: resolvedLayerThumbnails[option.id] || layerThumbnails?.[option.id],
      })),
    [layerThumbnails, resolvedLayerThumbnails]
  );

  const geofenceEditRef = useRef<{
    siteId: string;
    sourceId: string;
    fillLayerId: string;
    lineLayerId: string;
    vertexMarkers: mapboxgl.Marker[];
    ring: [number, number][];
    cleanup?: () => void;
  } | null>(null);

  const makeDefaultRing = useCallback((lng: number, lat: number): [number, number][] => {
    // ~55m box by default; scale longitude by latitude.
    const dLat = 0.0005;
    const dLng = dLat / Math.max(0.2, Math.cos((lat * Math.PI) / 180));
    const ring: [number, number][] = [
      [lng - dLng, lat - dLat],
      [lng + dLng, lat - dLat],
      [lng + dLng, lat + dLat],
      [lng - dLng, lat + dLat],
    ];
    ring.push([ring[0][0], ring[0][1]]);
    return ring;
  }, []);

  const sanitizeGeofenceCoordinates = useCallback((raw: string): [number, number][][] | null => {
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed) || parsed.length === 0) return null;

      const cleanedRings: [number, number][][] = [];
      for (const ringCandidate of parsed) {
        if (!Array.isArray(ringCandidate)) return null;

        const cleanedRing: [number, number][] = [];
        for (const point of ringCandidate) {
          const normalized = normalizeLngLatTuple(point);
          if (!normalized.ok) return null;
          cleanedRing.push(normalized.coord);
        }

        if (cleanedRing.length < 3) return null;

        const first = cleanedRing[0];
        const last = cleanedRing[cleanedRing.length - 1];
        const isClosed = first[0] === last[0] && first[1] === last[1];
        const closedRing = isClosed
          ? cleanedRing
          : [...cleanedRing, [first[0], first[1]] as [number, number]];

        const uniqueVertices = new Set(
          closedRing
            .slice(0, -1)
            .map(([lng, lat]) => `${lng.toFixed(7)},${lat.toFixed(7)}`)
        );
        if (uniqueVertices.size < 3) return null;

        cleanedRings.push(closedRing);
      }

      return cleanedRings;
    } catch {
      return null;
    }
  }, []);

  const parseRingFromSite = useCallback(
    (site: any): [number, number][] | null => {
      const raw = site?.geoFenceCoordinates;
      if (!raw || typeof raw !== 'string') return null;
      const coordinates = sanitizeGeofenceCoordinates(raw);
      return coordinates?.[0] || null;
    },
    [sanitizeGeofenceCoordinates]
  );

  const setEditSourceData = useCallback((map: mapboxgl.Map, sourceId: string, ring: [number, number][]) => {
    const data: GeoJSON.Feature<GeoJSON.Polygon> = {
      type: 'Feature',
      properties: {},
      geometry: { type: 'Polygon', coordinates: [ring] },
    };
    const src = map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined;
    if (src) src.setData(data);
  }, []);

  const cleanupGeofenceEdit = useCallback(() => {
    const edit = geofenceEditRef.current;
    const map = mapRef.current;
    if (!edit) return;
    try {
      edit.cleanup?.();
    } catch {
      // ignore
    }
    for (const m of edit.vertexMarkers) {
      try {
        m.remove();
      } catch {
        // ignore
      }
    }
    if (map) {
      if (map.getLayer(edit.lineLayerId)) map.removeLayer(edit.lineLayerId);
      if (map.getLayer(edit.fillLayerId)) map.removeLayer(edit.fillLayerId);
      if (map.getSource(edit.sourceId)) map.removeSource(edit.sourceId);
    }
    geofenceEditRef.current = null;
  }, []);

  const dist2 = (a: [number, number], b: [number, number]) => {
    const dx = a[0] - b[0];
    const dy = a[1] - b[1];
    return dx * dx + dy * dy;
  };

  const distToSeg2 = (p: [number, number], a: [number, number], b: [number, number]) => {
    const vx = b[0] - a[0];
    const vy = b[1] - a[1];
    const wx = p[0] - a[0];
    const wy = p[1] - a[1];
    const c1 = vx * wx + vy * wy;
    if (c1 <= 0) return dist2(p, a);
    const c2 = vx * vx + vy * vy;
    if (c2 <= c1) return dist2(p, b);
    const t = c1 / c2;
    const proj: [number, number] = [a[0] + t * vx, a[1] + t * vy];
    return dist2(p, proj);
  };

  const emitGeofenceEditChange = useCallback((ring: [number, number][]) => {
    // App expects a ring including closure.
    onGeofenceEditChange?.(ring);
  }, [onGeofenceEditChange]);

  useEffect(() => {
    const byId: Record<string, any> = {};
    for (const s of sites || []) byId[String(s._id)] = s;
    sitesByIdRef.current = byId;
  }, [sites]);

  const desiredStyleUrl =
    mapStyleMode === 'satellite'
      ? 'mapbox://styles/mapbox/standard-satellite'
      : 'mapbox://styles/mapbox/standard';

  const animateMarkers = useCallback(() => {
    animationFrameRef.current = undefined;
    if (document.hidden) return;
    // Use real timestamps so interpolation doesn't drift/jump on tab throttling.
    const now = Date.now();
    const displayTime = now - DISPLAY_OFFSET;
    let hasActiveInterpolation = false;
    let nextWakeInMs = Infinity;

    for (const id in vehicleHistoriesRef.current) {
      const hist = vehicleHistoriesRef.current[id];
      const marker = vehicleMarkersRef.current[id];
      if (!hist || hist.length === 0 || !marker) continue;

      const { position: pos, animating, wakeInMs } = sampleVehicleHistory(hist, displayTime);
      hasActiveInterpolation ||= animating;
      nextWakeInMs = Math.min(nextWakeInMs, wakeInMs);

      const lastAnimated = vehicleLastAnimatedCoordsRef.current[id];
      if (
        !lastAnimated ||
        Math.abs(lastAnimated[0] - pos[0]) > MARKER_POSITION_EPSILON ||
        Math.abs(lastAnimated[1] - pos[1]) > MARKER_POSITION_EPSILON
      ) {
        marker.setLngLat(pos);
        const dom = vehicleMarkerDomRefsRef.current[id];
        if (dom && mapRef.current) dom.icon.style.transform = `rotate(${screenVehicleHeading(mapRef.current, pos, dom.heading)}deg)`;
        vehicleLastAnimatedCoordsRef.current[id] = pos;
      }
    }

    if (hasActiveInterpolation) {
      animationFrameRef.current = requestAnimationFrame(animateMarkers);
      return;
    }

    // A delayed first segment needs one wakeup. Settled vehicles need none;
    // new GPS data restarts animation from the marker update effect.
    if (Number.isFinite(nextWakeInMs)) {
      animationTimeoutRef.current = window.setTimeout(() => {
        animationTimeoutRef.current = null;
        animationFrameRef.current = requestAnimationFrame(animateMarkers);
      }, Math.max(1, nextWakeInMs));
    }
  }, []);

  const restartMarkerAnimation = useCallback(() => {
    if (animationFrameRef.current != null) cancelAnimationFrame(animationFrameRef.current);
    if (animationTimeoutRef.current != null) clearTimeout(animationTimeoutRef.current);
    animationFrameRef.current = undefined;
    animationTimeoutRef.current = null;
    if (!document.hidden) animationFrameRef.current = requestAnimationFrame(animateMarkers);
  }, [animateMarkers]);

  // Add route to map
  const addRouteToMap = useCallback((route: any) => {
    if (!mapRef.current || !route) return;

    const map = mapRef.current;
    
    // Use a stable source/layer id so updating the route doesn't flicker
    const sourceId = routeSourceRef.current || 'active-route';
    routeSourceRef.current = sourceId;
    const layerId = `${sourceId}-layer`;

    // Accept multiple route shapes:
    // - Google Directions REST: route.overview_polyline.points (encoded polyline)
    // - Google Directions JS: route.overview_path (LatLng[])
    // - Client fallback: route.coordinates ([lng,lat][])
    let decodedCoordinates: [number, number][] = [];
    if (Array.isArray(route.coordinates)) {
      decodedCoordinates = route.coordinates;
    } else if (Array.isArray(route.overview_path)) {
      decodedCoordinates = route.overview_path
        .map((p: any) => {
          const lng = typeof p.lng === 'function' ? p.lng() : p.lng;
          const lat = typeof p.lat === 'function' ? p.lat() : p.lat;
          if (typeof lng !== 'number' || typeof lat !== 'number') return null;
          return [lng, lat] as [number, number];
        })
        .filter(Boolean) as [number, number][];
    } else if (route.overview_polyline?.points) {
      decodedCoordinates = decodePolyline(route.overview_polyline.points);
    }

    if (!decodedCoordinates || decodedCoordinates.length === 0) return;

    // Defensive: normalize/validate coordinates so a single bad point (e.g. 0,0) can't fling the map to the ocean.
    // Also auto-fix obvious [lat,lng] tuples if they slip through.
    const normalizedCoords: [number, number][] = [];
    for (const c of decodedCoordinates) {
      const n = normalizeLngLatTuple(c);
      if (n.ok) normalizedCoords.push(n.coord);
    }
    if (normalizedCoords.length === 0) return;
    decodedCoordinates = normalizedCoords;

    const data: GeoJSON.Feature<GeoJSON.LineString> = {
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: decodedCoordinates },
    };

    const existingSource = map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined;
    if (existingSource) {
      existingSource.setData(data);
    } else {
      map.addSource(sourceId, { type: 'geojson', data });
    }

    if (!map.getLayer(layerId)) {
      map.addLayer({
        id: layerId,
        type: 'line',
        source: sourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#007bff',
          'line-width': 4,
          'line-opacity': 0.8
        }
      });
    }

    // Zoom in to route (only when a new dispatch is started)
    if (shouldFitRouteRef.current) {
      const bounds = new mapboxgl.LngLatBounds(decodedCoordinates[0], decodedCoordinates[0]);
      decodedCoordinates.forEach(coord => bounds.extend(coord));
      // One-time zoom to show the full route (vehicle -> site). No follow after.
      map.fitBounds(bounds, { padding: 70, duration: 900, maxZoom: 17 });
      shouldFitRouteRef.current = false;
    }

  }, []);

  // Decode Google polyline
  const decodePolyline = (encoded: string): [number, number][] => {
    const poly: [number, number][] = [];
    let index = 0, len = encoded.length;
    let lat = 0, lng = 0;

    while (index < len) {
      let shift = 0, result = 0;

      do {
        let b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (result >= 0x20);

      let dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
      lat += dlat;

      shift = 0;
      result = 0;

      do {
        let b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (result >= 0x20);

      let dlng = ((result & 1) ? ~(result >> 1) : (result >> 1));
      lng += dlng;

      poly.push([lng / 1E5, lat / 1E5]);
    }

    return poly;
  };

  // Remove default POI markers
  const removeDefaultPOIMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    const hidePoiLayers = () => {
      try {
        const layers = map.getStyle()?.layers || [];
        layers.forEach((layer) => {
          const sourceLayer = (layer as any)['source-layer'];
          const isPoiLikeSymbolLayer =
            layer.type === 'symbol' &&
            (
              layer.id.includes('poi') ||
              layer.id === 'airport-label' ||
              layer.id === 'transit-label' ||
              layer.id.includes('landmark-icon') ||
              sourceLayer === 'landmarks_poi'
            );
          if (isPoiLikeSymbolLayer) {
            map.setLayoutProperty(layer.id, 'visibility', 'none');
          }
        });
      } catch {
        // Style may still be transitioning; ignore.
      }
    };

    if (!map.isStyleLoaded()) {
      try {
        map.once('style.load', hidePoiLayers);
      } catch {
        // Ignore event registration failures during teardown.
      }
      return;
    }

    hidePoiLayers();
  }, []);

  const applyBasemapConfig = useCallback((
    map: mapboxgl.Map,
    preset: MapLightPreset,
    styleMode: MapStyleMode
  ) => {
    const effectivePreset: MapLightPreset = styleMode === 'satellite' ? 'day' : preset;
    hqModelRef.current?.setLightPreset(preset);
    cookModelRef.current?.setLightPreset(preset);
    fifteenthModelRef.current?.setLightPreset(preset);
    frontierModelRef.current?.setLightPreset(preset);
    try {
      if (map.getConfigProperty('basemap', 'lightPreset') !== effectivePreset)
        map.setConfigProperty('basemap', 'lightPreset', effectivePreset);
    } catch {
      // Ignore on styles that do not support basemap config.
    }

    const settings =
      styleMode === 'satellite'
        ? BASEMAP_SATELLITE_SETTINGS
        : BASEMAP_STANDARD_SETTINGS;

    Object.entries(settings).forEach(([key, value]) => {
      try {
        if (map.getConfigProperty('basemap', key) !== value)
          map.setConfigProperty('basemap', key, value);
      } catch {
        // Ignore unsupported keys/style types.
      }
    });
  }, []);

  const handleMapLayerSelection = useCallback((selection: MapLayerSelection) => {
    if (selection === 'satellite') {
      onMapStyleModeChange('satellite');
      return;
    }

    const map = mapRef.current;

    setLightPreset(selection);
    onMapStyleModeChange('standard');

    if (!map) return;
    applyBasemapConfig(map, selection, 'standard');
    removeDefaultPOIMarkers();
  }, [applyBasemapConfig, onMapStyleModeChange, removeDefaultPOIMarkers]);

  const parseGeofenceCoordinates = useCallback((raw: string): number[][][] | null => {
    const cached = geofenceParseCacheRef.current.get(raw);
    if (cached !== undefined) return cached;
    const valid = sanitizeGeofenceCoordinates(raw);
    geofenceParseCacheRef.current.set(raw, valid);
    return valid;
  }, [sanitizeGeofenceCoordinates]);

  // Update all geofences as a single combined layer (much more efficient)
  const updateAllGeofences = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    const geofenceSignature = showGeofences
      ? sites
          .map((site) => `${String(site?._id || '')}:${String(site?.geoFenceCoordinates || '')}`)
          .join('|')
      : '__geofences_hidden__';

    const existingSource = map.getSource(allGeofencesSourceId) as mapboxgl.GeoJSONSource | undefined;
    const hasFillLayer = !!map.getLayer(allGeofencesLayerId);
    const hasBorderLayer = !!map.getLayer(allGeofencesBorderLayerId);
    const filter = modeledSiteGeofenceFilter(mapStyleMode === 'satellite');
    // Keep source data intact so Satellite can restore every site's fence.
    for (const id of [allGeofencesLayerId, allGeofencesBorderLayerId]) {
      if (map.getLayer(id) && JSON.stringify(map.getFilter(id) ?? null) !== JSON.stringify(filter)) {
        map.setFilter(id, filter);
      }
    }
    if (
      existingSource &&
      hasFillLayer &&
      hasBorderLayer &&
      lastRenderedGeofenceSignatureRef.current === geofenceSignature
    ) {
      return;
    }

    // Build a FeatureCollection with all geofences
    const features: GeoJSON.Feature<GeoJSON.Polygon>[] = [];
    
    if (showGeofences) {
      sites.forEach(site => {
        if (site.geoFenceCoordinates) {
          const coordinates = parseGeofenceCoordinates(site.geoFenceCoordinates);
          if (coordinates) {
            features.push({
              type: 'Feature',
              properties: {
                siteId: String(site._id),
                siteName: site.name
              },
              geometry: {
                type: 'Polygon',
                coordinates: coordinates
              }
            });
          } else {
            try {
              JSON.parse(site.geoFenceCoordinates);
            } catch (error) {
              console.error('Error parsing geofence for site:', site.name, error);
            }
          }
        }
      });
    }

    const featureCollection: GeoJSON.FeatureCollection<GeoJSON.Polygon> = {
      type: 'FeatureCollection',
      features: features
    };

    // Update or create the combined source
    if (existingSource) {
      existingSource.setData(featureCollection);
    } else {
      map.addSource(allGeofencesSourceId, {
        type: 'geojson',
        data: featureCollection
      });
    }

    // Add fill layer if it doesn't exist
    if (!map.getLayer(allGeofencesLayerId)) {
      map.addLayer({
        id: allGeofencesLayerId,
        ...(filter ? { filter } : {}),
        type: 'fill',
        source: allGeofencesSourceId,
        slot: regularGeofenceSlot,
        paint: {
          'fill-color': '#8B0000',
          'fill-opacity': 0.4
        }
      } as mapboxgl.AnyLayer);
    }

    // Add border layer if it doesn't exist
    if (!map.getLayer(allGeofencesBorderLayerId)) {
      map.addLayer({
        id: allGeofencesBorderLayerId,
        ...(filter ? { filter } : {}),
        type: 'line',
        source: allGeofencesSourceId,
        slot: regularGeofenceSlot,
        paint: {
          'line-color': '#8B0000',
          'line-width': 2,
          'line-opacity': 0.8
        }
      } as mapboxgl.AnyLayer);
    }

    lastRenderedGeofenceSignatureRef.current = geofenceSignature;
  }, [sites, showGeofences, mapStyleMode, parseGeofenceCoordinates]);

  // Setup click handlers for the combined geofences layer
  const setupGeofenceClickHandlers = useCallback(() => {
    const map = mapRef.current;
    if (!map || !geofenceEditorEnabledRef.current || !onGeofencePickForEdit) return;

    // Remove existing handlers
    const handlers = geofenceClickHandlersRef.current.get(allGeofencesLayerId);
    if (handlers) {
      try {
        if (handlers.click) map.off('click', allGeofencesLayerId, handlers.click);
        if (handlers.mouseenter) map.off('mouseenter', allGeofencesLayerId, handlers.mouseenter);
        if (handlers.mouseleave) map.off('mouseleave', allGeofencesLayerId, handlers.mouseleave);
      } catch {
        // ignore
      }
    }

    // Add new handlers
    const clickHandler = (e: any) => {
      if (e.defaultPrevented) return;
      if (isSiteMarkerHit(map, e.point)) return;
      if (!e.features || e.features.length === 0) return;
      const siteId = e.features[0].properties?.siteId;
      if (siteId) {
        e.preventDefault();
        onGeofencePickForEdit(siteId);
      }
    };

    const mouseEnterHandler = () => {
      map.getCanvas().style.cursor = 'pointer';
    };

    const mouseLeaveHandler = () => {
      map.getCanvas().style.cursor = '';
    };

    map.on('click', allGeofencesLayerId, clickHandler);
    map.on('mouseenter', allGeofencesLayerId, mouseEnterHandler);
    map.on('mouseleave', allGeofencesLayerId, mouseLeaveHandler);

    geofenceClickHandlersRef.current.set(allGeofencesLayerId, {
      click: clickHandler,
      mouseenter: mouseEnterHandler,
      mouseleave: mouseLeaveHandler
    });
  }, [onGeofencePickForEdit]);

  useEffect(() => {
    if (mapRef.current || !mapContainerRef.current) return;

    // Initialize map with original styling
    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: { version: 8, imports: [{ id: 'basemap', url: desiredStyleUrl }], sources: {}, layers: [] },
      center: [-120.9969, 37.6391],
      zoom: 10,
      projection: 'mercator',
      // Do not pull the bearing back to north when a rotation ends nearby.
      bearingSnap: 0,
      // Avoid a multisampled full-resolution framebuffer on high-DPI displays.
      // This changes edge smoothing, never geographic model size or detail.
      antialias: false,
      config: {
        basemap: {
          lightPreset,
          ...(mapStyleMode === 'satellite'
            ? BASEMAP_SATELLITE_SETTINGS
            : BASEMAP_STANDARD_SETTINGS),
        },
      }
    });

    const map = mapRef.current;
    const disposeRenderQuality = attachRenderQuality(map);
    bodycamLayerRef.current = attachBodycamLayer(map, id => bodycamSelectRef.current?.(id));
    // Reduce abrupt accumulated scroll deltas without replacing native easing.
    map.scrollZoom.setZoomRate(1 / 150);
    map.scrollZoom.setWheelZoomRate(1 / 650);
    lastStyleModeRef.current = mapStyleMode;
    hqModelRef.current = attachApsHqModel(map, mapStyleMode, setHqModelError, lightPreset);
    cookModelRef.current = attachCookMarketplaceModel(map, mapStyleMode, setCookModelError, lightPreset);
    fifteenthModelRef.current = attachFifteenthStreetModel(map, mapStyleMode, setFifteenthModelError, lightPreset);
    frontierModelRef.current = attachFrontierTownModel(map, mapStyleMode, setFrontierModelError, lightPreset);
    siteMarkerLayerRef.current = attachSiteMarkerLayer(map, id => {
      const handlers = siteSelectRef.current;
      if (geofenceEditorEnabledRef.current && handlers.onGeofencePickForEdit) handlers.onGeofencePickForEdit(id);
      else handlers.onSelectSite(id);
    });

    // Prevent black flashes during resize by giving the WebGL canvas a non-black fallback.
    // (Tiles may still be re-rendering; this avoids a jarring black frame.)
    try {
      const canvas = map.getCanvas();
      canvas.style.backgroundColor = '#141414';
    } catch {
      // ignore
    }

    // Disable marker transition during user map interactions (from original)
    // Consolidated into single handler for better performance
    const disableMarkerTransitions = () => {
      const markers = Object.values(vehicleMarkersRef.current);
      if (markers.length === 0) return;
      for (const marker of markers) {
        (marker.getElement() as HTMLElement).style.transition = 'none';
      }
    };

    // setScaleFactor changes symbol sizes and triggers label layout; it is
    // not a render-resolution control. Keep label scale stable while moving.
    map.on('movestart', disableMarkerTransitions);
    document.addEventListener('visibilitychange', restartMarkerAnimation);

    // Create custom navigation controls (from original)
    map.on('load', () => {
      map.addControl(new MapNavigationControl(), 'bottom-right');

      // Set style properties and add controls
      applyBasemapConfig(map, lightPreset, mapStyleMode);
      const controlSelection: MapLayerSelection =
        mapStyleMode === 'satellite' ? 'satellite' : lightPreset;
      styleDropdownControlRef.current = new StyleDropdownControl(
        handleMapLayerSelection,
        controlSelection,
        layerOptions
      );
      map.addControl(styleDropdownControlRef.current, 'top-right');
      removeDefaultPOIMarkers();

    });

    const updateVehicleCameraHeadings = () => {
      for (const [id, dom] of Object.entries(vehicleMarkerDomRefsRef.current)) {
        const marker = vehicleMarkersRef.current[id];
        if (!marker) continue;
        const pos = marker.getLngLat();
        dom.icon.style.transform = `rotate(${screenVehicleHeading(map, [pos.lng, pos.lat], dom.heading)}deg)`;
      }
    };
    map.on('move', updateVehicleCameraHeadings);

    // Start animation
    animationFrameRef.current = requestAnimationFrame(animateMarkers);

    return () => {
      map.off('move', updateVehicleCameraHeadings);
      disposeRenderQuality();
      bodycamLayerRef.current?.dispose();
      bodycamLayerRef.current = null;
      map.off('movestart', disableMarkerTransitions);
      document.removeEventListener('visibilitychange', restartMarkerAnimation);
      styleDropdownControlRef.current = null;
      siteMarkerLayerRef.current?.dispose(false);
      siteMarkerLayerRef.current = null;
      hqModelRef.current?.dispose();
      hqModelRef.current = null;
      cookModelRef.current?.dispose();
      cookModelRef.current = null;
      frontierModelRef.current?.dispose();
      frontierModelRef.current = null;
      fifteenthModelRef.current?.dispose();
      fifteenthModelRef.current = null;
      mapRef.current?.remove();
      mapRef.current = null;
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (animationTimeoutRef.current != null) clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    };
  }, [
    animateMarkers,
    restartMarkerAnimation,
    applyBasemapConfig,
    handleMapLayerSelection,
    removeDefaultPOIMarkers
  ]);

  useEffect(() => {
    const control = styleDropdownControlRef.current;
    if (!control) return;
    control.syncSelection(mapStyleMode === 'satellite' ? 'satellite' : lightPreset);
  }, [lightPreset, mapStyleMode]);

  useEffect(() => {
    const control = styleDropdownControlRef.current;
    if (!control) return;
    control.setOptions(layerOptions);
  }, [layerOptions]);

  // Carry app overlays through an atomic style swap. Never restore an old
  // camera snapshot or defer overlay reconstruction until tiles become idle.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      // A fragment event can precede completion of the initial root style.
      try {
        if (!map.getStyle()?.imports?.some(item => item.id === 'basemap')) return;
      } catch { return; }
      hqModelRef.current?.setMode(mapStyleMode);
      cookModelRef.current?.setMode(mapStyleMode);
      fifteenthModelRef.current?.setMode(mapStyleMode);
      frontierModelRef.current?.setMode(mapStyleMode);
      if (lastStyleModeRef.current !== mapStyleMode) {
        const previous = lastStyleModeRef.current;
        lastStyleModeRef.current = mapStyleMode;
        try {
          const current = map.getStyle();
          map.setStyle({
            version: 8,
            imports: [{ id: 'basemap', url: desiredStyleUrl, config: {
              lightPreset: mapStyleMode === 'satellite' ? 'day' : lightPreset,
              ...(mapStyleMode === 'satellite' ? BASEMAP_SATELLITE_SETTINGS : BASEMAP_STANDARD_SETTINGS),
            } }],
            // Carry live overlay data (including edited rings/routes) into the
            // replacement before it renders. Reset placement atomically.
            sources: current.sources,
            layers: current.layers,
          }, { diff: false, localFontFamily: undefined, localIdeographFontFamily: 'sans-serif' });
        }
        catch (error) { lastStyleModeRef.current = previous; throw error; }
      }
      applyBasemapConfig(map, lightPreset, mapStyleMode);
    };
    // Layer presence is not tied to all visible tiles being downloaded.
    // Register before loading; cleanup cancels stale selections on rapid toggles.
    map.on('style.load', apply);
    map.on('style.import.load', apply);
    apply();
    return () => {
      map.off('style.load', apply);
      map.off('style.import.load', apply);
    };
  }, [mapStyleMode, desiredStyleUrl, lightPreset, applyBasemapConfig]);

  // Update route when dispatch result changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const sync = () => {
      try { map.getStyle(); } catch { return; }
    if (dispatchResult && dispatchResult.optimalVehicle && dispatchResult.optimalVehicle.route) {
      // New route: zoom in once.
      if (!routeWasDrawnRef.current) {
        shouldFitRouteRef.current = true;
        routeWasDrawnRef.current = true;
      }
      addRouteToMap(dispatchResult.optimalVehicle.route);
    } else if (routeSourceRef.current && mapRef.current) {
      // Remove route when no dispatch result
      const map = mapRef.current;
      const sourceId = routeSourceRef.current;
      const layerId = `${sourceId}-layer`;
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
      routeSourceRef.current = null;
      shouldFitRouteRef.current = false;
      routeWasDrawnRef.current = false;
    }
    };
    map.on('style.load', sync);
    sync();
    return () => { map.off('style.load', sync); };
  }, [dispatchResult, addRouteToMap]);

  // Update vehicle markers
  useEffect(() => {
    if (!mapRef.current) return;

    const currentMap = mapRef.current;
    const currentMarkers = vehicleMarkersRef.current;
    const vehicleHistories = vehicleHistoriesRef.current;
    const vehicleMarkerDomRefs = vehicleMarkerDomRefsRef.current;

    const removeVehicleMarker = (vehicleId: string) => {
      try {
        currentMarkers[vehicleId]?.remove();
      } catch {
        // ignore
      }
      delete currentMarkers[vehicleId];
      delete vehicleHistories[vehicleId];
      delete vehicleMarkerCreatingRef.current[vehicleId];
      delete vehicleLastAnimatedCoordsRef.current[vehicleId];
      delete vehicleMarkerDomRefs[vehicleId];
    };

    const visibleVehiclesByPublicId = new globalThis.Map<string, any>();
    for (const vehicle of vehicles) {
      const id = vehicle.vehicleId;
      if (!id || !vehicle.isVisible) continue;
      visibleVehiclesByPublicId.set(id, vehicle);
    }

    // Remove markers for vehicles that no longer exist or are not visible
    Object.keys(currentMarkers).forEach((vehicleId) => {
      if (!visibleVehiclesByPublicId.has(vehicleId)) {
        removeVehicleMarker(vehicleId);
      }
    });

    for (const [id, vehicle] of visibleVehiclesByPublicId.entries()) {
      // Prefer the backend sample time so marker interpolation is consistent.
      // `lastMovementTime` is set whenever the telematics fields change.
      let sampleTime = typeof vehicle.lastMovementTime === 'number' ? vehicle.lastMovementTime : Date.now();
      const coord: [number, number] = [vehicle.longitude, vehicle.latitude];
      
      const hist = vehicleHistories[id] = vehicleHistories[id] || [];
      const last = hist[hist.length - 1];

      if (!last || last.coord[0] !== coord[0] || last.coord[1] !== coord[1]) {
          if (last && sampleTime <= last.time) {
            // Ensure monotonic timestamps to avoid divide-by-zero and sudden jumps.
            sampleTime = last.time + 1;
          }

          // If we missed a bunch of updates (tab throttling / network hiccup),
          // reset history so we don't animate a huge "catch-up" jump.
          if (last && sampleTime - last.time > 15000) {
            hist.length = 0;
          }

          hist.push({ time: sampleTime, coord });
          if (hist.length > MAX_HISTORY) {
              hist.shift();
          }
      }

      const imageUrl = vehicle.isMoving 
        ? (assetUrls?.Vehicle_IsMoving || FALLBACK_ASSETS.Vehicle_IsMoving)
        : (assetUrls?.Vehicle_NotMoving || FALLBACK_ASSETS.Vehicle_NotMoving);
      const heading = normalizeHeadingDegrees(vehicle.heading);

      const driverName = (vehicle.driverName || '').trim();
      const vehicleTitle = vehicle.displayName || vehicle.vehicleId || 'Vehicle';
      const labelText = driverName || vehicleTitle;
      const targetZIndex = vehicle._id === selectedVehicleId ? '60' : '50';

      if (currentMarkers[id]) {
        // Update existing marker
        const marker = currentMarkers[id];
        let dom = vehicleMarkerDomRefs[id];
        if (!dom) {
          const markerElement = marker.getElement() as HTMLElement;
          const markerLabel = markerElement.querySelector('.vehicle-marker-label') as HTMLElement | null;
          const markerIcon = markerElement.querySelector('.vehicle-marker-icon') as HTMLElement | null;
          if (!markerLabel || !markerIcon) continue;
          dom = { wrapper: markerElement, label: markerLabel, icon: markerIcon, heading };
          vehicleMarkerDomRefs[id] = dom;
        }

        if (dom.label.textContent !== labelText) {
          dom.label.textContent = labelText;
        }

        if (dom.wrapper.dataset.iconUrl !== imageUrl) {
          dom.icon.style.backgroundImage = `url(${imageUrl})`;
          dom.wrapper.dataset.iconUrl = imageUrl;
        }

        dom.heading = heading;
        const location = marker.getLngLat();
        const headingTransform = `rotate(${screenVehicleHeading(currentMap, [location.lng, location.lat], heading)}deg)`;
        if (dom.icon.style.transform !== headingTransform) {
          dom.icon.style.transform = headingTransform;
        }

        // Apply selected style without changing size/transform (avoids any perceived map "jump")
        if (dom.wrapper.style.zIndex !== targetZIndex) {
          dom.wrapper.style.zIndex = targetZIndex;
        }
      } else {
        // Guard: if we're already creating this marker, don't start another.
        if (vehicleMarkerCreatingRef.current[id]) continue;
        vehicleMarkerCreatingRef.current[id] = true;

        try {
          // Create new marker only if it doesn't exist
          const el = document.createElement('div');
          el.className = 'marker vehicle-marker';
          el.style.cursor = 'pointer';
          el.style.pointerEvents = 'auto';

          const labelEl = document.createElement('div');
          labelEl.className = 'vehicle-marker-label';
          labelEl.textContent = labelText;

          const iconEl = document.createElement('div');
          iconEl.className = 'vehicle-marker-icon';
          iconEl.style.backgroundImage = `url(${imageUrl})`;
          iconEl.style.transform = `rotate(${screenVehicleHeading(currentMap, hist[hist.length - 1].coord, heading)}deg)`;

          el.appendChild(labelEl);
          el.appendChild(iconEl);
          el.dataset.iconUrl = imageUrl;
          el.style.zIndex = targetZIndex;

          el.addEventListener('click', (e) => {
            e.stopPropagation();
            onSelectVehicle(vehicle._id);
          });

          const newMarker = new mapboxgl.Marker({
            element: el,
            anchor: 'center'
          })
            .setLngLat(hist[hist.length - 1].coord)
            .addTo(currentMap);

          // Ensure the marker wrapper has pointer events enabled and proper z-index
          const markerElement = newMarker.getElement();
          markerElement.style.pointerEvents = 'auto';
          markerElement.style.zIndex = targetZIndex;

          currentMarkers[id] = newMarker;
          vehicleLastAnimatedCoordsRef.current[id] = hist[hist.length - 1].coord;
          vehicleMarkerDomRefs[id] = { wrapper: markerElement, label: labelEl, icon: iconEl, heading };
        } finally {
          vehicleMarkerCreatingRef.current[id] = false;
        }
      }
    }
    restartMarkerAnimation();
  }, [vehicles, selectedVehicleId, onSelectVehicle, assetUrls?.Vehicle_IsMoving, assetUrls?.Vehicle_NotMoving, restartMarkerAnimation]);

  // Static site icons share one GPU layer instead of per-frame DOM markers.
  useEffect(() => {
    const points = sites
      .filter(site => String(site._id) !== markerEditDraft?.siteId)
      .filter(site => !/^aps hq(?: 2)?$/.test((site.name || '').trim().toLowerCase().replace(/\s+/g, ' ')))
      .map(site => ({
        id: String(site._id), name: site.name, longitude: siteMarkerPosition(site)[0], latitude: siteMarkerPosition(site)[1],
        iconUrl: getSiteMarkerIconUrl(site.siteType), size: getSiteMarkerSizePx(site.siteType),
        priority: Number(getSiteMarkerZIndex(site._id, site.siteType)),
      }));
    siteMarkerLayerRef.current?.update(points, showSiteMarkers);
  }, [sites, showSiteMarkers, markerEditDraft?.siteId, getSiteMarkerIconUrl, getSiteMarkerSizePx, getSiteMarkerZIndex]);

  // Only the actively edited site uses a DOM marker; the remaining sites stay on the GPU.
  const editMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const markerEditStateRef = useRef({ markerEditDraft, onMarkerEditChange });
  markerEditStateRef.current = { markerEditDraft, onMarkerEditChange };
  useEffect(() => {
    const map = mapRef.current;
    const draft = markerEditStateRef.current.markerEditDraft;
    if (!map || !draft) return;
    const marker = new mapboxgl.Marker({ color: '#e74859', draggable: true })
      .setLngLat(draft.coordinates).addTo(map);
    editMarkerRef.current = marker;
    marker.getElement().style.cursor = 'grab';
    marker.getElement().setAttribute('aria-label', 'Drag site marker to a new location');
    marker.on('dragend', () => {
      const { lng, lat } = marker.getLngLat();
      markerEditStateRef.current.onMarkerEditChange?.([((lng + 180) % 360 + 360) % 360 - 180, lat]);
    });
    map.easeTo({ center: draft.coordinates, zoom: Math.max(map.getZoom(), 17), duration: 850 });
    return () => { marker.remove(); editMarkerRef.current = null; };
  }, [markerEditDraft?.siteId]);
  useEffect(() => {
    if (markerEditDraft) editMarkerRef.current?.setLngLat(markerEditDraft.coordinates);
    editMarkerRef.current?.setDraggable(!markerEditSaving);
  }, [markerEditDraft, markerEditSaving]);

  // RSPNDR destination marker (address dispatch)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const lat = rspndrDestination?.lat;
    const lng = rspndrDestination?.lng;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      if (rspndrMarkerRef.current) {
        try {
          rspndrMarkerRef.current.remove();
        } catch {
          // ignore
        }
        rspndrMarkerRef.current = null;
      }
      return;
    }

    if (!rspndrMarkerRef.current) {
      const el = document.createElement('div');
      el.className = 'marker site-marker rspndr-marker';
      el.style.width = `${siteMarkerSizePx}px`;
      el.style.height = `${siteMarkerSizePx}px`;
      el.style.backgroundImage = `url(${assetUrls?.SiteIcon || FALLBACK_ASSETS.SiteIcon})`;
      el.style.backgroundSize = 'contain';
      el.style.cursor = 'default';
      el.style.position = 'absolute';
      el.style.pointerEvents = 'none';

      const marker = new mapboxgl.Marker({
        element: el,
        anchor: 'center',
      })
        .setLngLat([lng as number, lat as number])
        .addTo(map);

      const markerElement = marker.getElement();
      if (markerElement) {
        markerElement.style.pointerEvents = 'none';
        markerElement.style.zIndex = '25';
      }

      rspndrMarkerRef.current = marker;
    } else {
      rspndrMarkerRef.current.setLngLat([lng as number, lat as number]);
      const markerElement = rspndrMarkerRef.current.getElement() as HTMLElement | null;
      if (markerElement) {
        markerElement.style.width = `${siteMarkerSizePx}px`;
        markerElement.style.height = `${siteMarkerSizePx}px`;
        markerElement.style.backgroundImage = `url(${assetUrls?.SiteIcon || FALLBACK_ASSETS.SiteIcon})`;
        markerElement.style.backgroundSize = 'contain';
      }
    }
  }, [rspndrDestination?.lat, rspndrDestination?.lng, assetUrls?.SiteIcon, siteMarkerSizePx]);

  // Update geofences when showGeofences or sites change
  // NOTE: geofenceEditSiteId is NOT in dependencies - the edit useEffect handles updating geofences
  useEffect(() => {
    if (!mapRef.current) return;
    
    const map = mapRef.current;
    const restore = () => updateAllGeofences();
    map.on('style.load', restore);
    if (map.isStyleLoaded() || map.getSource(allGeofencesSourceId)) restore();
    return () => { map.off('style.load', restore); };
  }, [showGeofences, sites, updateAllGeofences]);

  // Update the ref when geofence editor mode changes
  useEffect(() => {
    geofenceEditorEnabledRef.current = geofenceEditorEnabled && showGeofences;
  }, [geofenceEditorEnabled, showGeofences]);

  // When geofence editor mode is enabled, attach click handlers to existing geofences
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    
    if (!geofenceEditorEnabled || !showGeofences || !onGeofencePickForEdit) {
      // Clean up any existing handlers
      geofenceClickHandlersRef.current.forEach((handlers, layerId) => {
        try {
          if (handlers.click) map.off('click', layerId, handlers.click);
          if (handlers.mouseenter) map.off('mouseenter', layerId, handlers.mouseenter);
          if (handlers.mouseleave) map.off('mouseleave', layerId, handlers.mouseleave);
        } catch {
          // ignore
        }
      });
      geofenceClickHandlersRef.current.clear();
      map.getCanvas().style.cursor = '';
      return;
    }

    // Setup click handlers for the combined geofences layer
    setupGeofenceClickHandlers();
  }, [geofenceEditorEnabled, showGeofences, onGeofencePickForEdit, setupGeofenceClickHandlers]);

  // Track edit mode changes (cleanup is handled by the main edit useEffect)
  const prevEditSiteIdRef = useRef<string | null>(null);
  useEffect(() => {
    prevEditSiteIdRef.current = geofenceEditSiteId ?? null;
  }, [geofenceEditSiteId]);

  // Geofence editing mode (vertex drag + shift-click add + right-click delete)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const siteId = geofenceEditSiteId || null;
    if (!siteId) {
      cleanupGeofenceEdit();
      return;
    }

    const site = sitesByIdRef.current[String(siteId)];
    if (!site) {
      cleanupGeofenceEdit();
      return;
    }

    const start = () => {
      cleanupGeofenceEdit();

      const n = normalizeLatLng(site.latitude, site.longitude, { allowZeroZero: false });
      const defaultRing = n.ok ? makeDefaultRing(n.lng, n.lat) : makeDefaultRing(-120.9969, 37.6391);
      const ring = parseRingFromSite(site) || defaultRing;

      const sourceId = `geofence-edit-${siteId}`;
      const fillLayerId = `${sourceId}-fill`;
      const lineLayerId = `${sourceId}-line`;

      if (!map.getSource(sourceId)) {
        map.addSource(sourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: { siteId: String(siteId), siteName: site.name },
            geometry: { type: 'Polygon', coordinates: [ring] },
          },
        });
      }

      if (!map.getLayer(fillLayerId)) {
        map.addLayer({
          id: fillLayerId,
          type: 'fill',
          source: sourceId,
          slot: editGeofenceSlot,
          paint: {
            'fill-color': '#8B0000',
            'fill-opacity': 0.5,  // Slightly higher opacity for edit mode
          },
        } as mapboxgl.AnyLayer);
      }

      if (!map.getLayer(lineLayerId)) {
        map.addLayer({
          id: lineLayerId,
          type: 'line',
          source: sourceId,
          slot: editGeofenceSlot,
          paint: {
            'line-color': '#FFFFFF',  // White border for edit mode visibility
            'line-width': 3,
            'line-opacity': 1.0,
          },
        } as mapboxgl.AnyLayer);
      }

      // Ensure edit layers are on top and remain interactive across different styles.
      // This is critical for maintaining clickability after style switches (satellite view).
      try {
        if (map.getLayer(fillLayerId)) map.moveLayer(fillLayerId);
        if (map.getLayer(lineLayerId)) map.moveLayer(lineLayerId);
        map.triggerRepaint();
      } catch (e) {
        console.warn('Error moving layers to top:', e);
      }

      const vertexMarkers: mapboxgl.Marker[] = [];
      const rebuildVertexMarkers = (currentRing: [number, number][]) => {
        for (const m of vertexMarkers) m.remove();
        vertexMarkers.length = 0;

        // Exclude closure point.
        const count = Math.max(0, currentRing.length - 1);
        for (let i = 0; i < count; i++) {
          const el = document.createElement('div');
          el.className = 'geofence-vertex-marker';
          el.title = 'Drag to adjust. Right-click to delete. Shift-click map to add a point.';
          el.style.pointerEvents = 'auto';
          el.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const edit = geofenceEditRef.current;
            if (!edit) return;
            // Need at least 3 vertices.
            const verts = edit.ring.slice(0, -1);
            if (verts.length <= 3) return;
            verts.splice(i, 1);
            const nextRing = [...verts, [verts[0][0], verts[0][1]] as [number, number]];
            edit.ring = nextRing;
            setEditSourceData(map, edit.sourceId, nextRing);
            rebuildVertexMarkers(nextRing);
            emitGeofenceEditChange(nextRing);
          });

          const marker = new mapboxgl.Marker({ element: el, draggable: true, anchor: 'center' })
            .setLngLat(currentRing[i])
            .addTo(map);
          try {
            const wrapper = marker.getElement();
            wrapper.style.zIndex = '9999';
            wrapper.style.pointerEvents = 'auto';
          } catch {
            // ignore
          }

          marker.on('drag', () => {
            const edit = geofenceEditRef.current;
            if (!edit) return;
            const ll = marker.getLngLat();
            const verts = edit.ring.slice(0, -1);
            verts[i] = [ll.lng, ll.lat];
            const nextRing = [...verts, [verts[0][0], verts[0][1]] as [number, number]];
            edit.ring = nextRing;
            setEditSourceData(map, edit.sourceId, nextRing);
          });

          // Avoid pushing React state updates on every drag tick (keeps editing snappy).
          marker.on('dragend', () => {
            const edit = geofenceEditRef.current;
            if (!edit) return;
            emitGeofenceEditChange(edit.ring);
          });

          vertexMarkers.push(marker);
        }
      };

      const onMapClick = (e: mapboxgl.MapMouseEvent) => {
        if (isSiteMarkerHit(map, e.point)) return;
        const isShift = (e.originalEvent as MouseEvent).shiftKey;
        // Easy add: click on the geofence itself to insert a point (no modifier).
        // Fallback: Shift-click anywhere to insert a point.
        if (!isShift) {
          try {
            const feats = map.queryRenderedFeatures(e.point, { layers: [fillLayerId] });
            if (!feats || feats.length === 0) return;
          } catch {
            return;
          }
        }
        const edit = geofenceEditRef.current;
        if (!edit) return;
        const click: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        const verts = edit.ring.slice(0, -1);
        if (verts.length < 3) return;

        // Insert at nearest segment.
        let bestI = 0;
        let bestD = Number.POSITIVE_INFINITY;
        for (let i = 0; i < verts.length; i++) {
          const a = verts[i];
          const b = verts[(i + 1) % verts.length];
          const d = distToSeg2(click, a, b);
          if (d < bestD) {
            bestD = d;
            bestI = i;
          }
        }
        verts.splice(bestI + 1, 0, click);
        const nextRing = [...verts, [verts[0][0], verts[0][1]] as [number, number]];
        edit.ring = nextRing;
        setEditSourceData(map, edit.sourceId, nextRing);
        rebuildVertexMarkers(nextRing);
        emitGeofenceEditChange(nextRing);
      };

      map.on('click', onMapClick);

      const ensureLayers = () => {
        const edit = geofenceEditRef.current;
        if (!edit) return;
        
        // Wait for style to be fully loaded before manipulating layers
        if (!map.isStyleLoaded()) return;
        
        // If style changed, sources/layers can be dropped; re-add if needed.
        if (!map.getSource(edit.sourceId)) {
          map.addSource(edit.sourceId, {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: { siteId: String(siteId), siteName: site.name },
              geometry: { type: 'Polygon', coordinates: [edit.ring] },
            },
          });
        } else {
          setEditSourceData(map, edit.sourceId, edit.ring);
        }
        
        // Re-add fill layer if missing
        if (!map.getLayer(edit.fillLayerId)) {
          map.addLayer({
            id: edit.fillLayerId,
            type: 'fill',
            source: edit.sourceId,
            slot: editGeofenceSlot,
            paint: {
              'fill-color': '#8B0000',
              'fill-opacity': 0.5,  // Slightly higher opacity for edit mode
            },
          } as mapboxgl.AnyLayer);
        }
        
        // Re-add line layer if missing
        if (!map.getLayer(edit.lineLayerId)) {
          map.addLayer({
            id: edit.lineLayerId,
            type: 'line',
            source: edit.sourceId,
            slot: editGeofenceSlot,
            paint: {
              'line-color': '#FFFFFF',  // White border for edit mode visibility
              'line-width': 3,
              'line-opacity': 1.0,
            },
          } as mapboxgl.AnyLayer);
        }

        // Move layers to absolute top to ensure clickability
        try {
          if (map.getLayer(edit.fillLayerId)) {
            map.moveLayer(edit.fillLayerId);
          }
          if (map.getLayer(edit.lineLayerId)) {
            map.moveLayer(edit.lineLayerId);
          }
        } catch (e) {
          // Silently ignore - layers may not be ready yet
        }
      };
      
      // Listen to style changes to re-add layers
      map.on('style.load', ensureLayers);
      map.on('styledata', ensureLayers);
      
      // CRITICAL: Also listen to idle event to ensure layers stay on top after tiles load
      const onIdle = () => {
        const edit = geofenceEditRef.current;
        if (!edit || !map.isStyleLoaded()) return;
        
        // Ensure layers are still on top after all rendering is complete
        try {
          if (map.getLayer(edit.fillLayerId)) {
            map.moveLayer(edit.fillLayerId);
          }
          if (map.getLayer(edit.lineLayerId)) {
            map.moveLayer(edit.lineLayerId);
          }
        } catch (e) {
          // ignore
        }
      };
      map.on('idle', onIdle);
      
      // Move layers to top once initially and on idle (much more efficient than render events)
      const moveEditLayersToTop = () => {
        const edit = geofenceEditRef.current;
        if (!edit || !map.isStyleLoaded()) return;
        
        try {
          // Move to ABSOLUTE TOP - no beforeId parameter
          if (map.getLayer(edit.fillLayerId)) {
            map.moveLayer(edit.fillLayerId);
          }
          if (map.getLayer(edit.lineLayerId)) {
            map.moveLayer(edit.lineLayerId);
          }
        } catch (e) {
          // Silently ignore - layers may not exist yet
        }
      };
      
      // Move layers to top immediately
      moveEditLayersToTop();

      geofenceEditRef.current = {
        siteId: String(siteId),
        sourceId,
        fillLayerId,
        lineLayerId,
        vertexMarkers,
        ring,
        cleanup: () => {
          map.off('click', onMapClick);
          map.off('style.load', ensureLayers);
          map.off('styledata', ensureLayers);
          map.off('idle', onIdle);
        },
      };

      rebuildVertexMarkers(ring);
      emitGeofenceEditChange(ring);
    };

    if (!map.isStyleLoaded()) {
      let started = false;
      const maybeStart = () => {
        if (started) return;
        started = true;
        start();
        try {
          map.off('style.load', maybeStart);
          map.off('styledata', maybeStart);
        } catch {
          // ignore
        }
      };
      map.on('style.load', maybeStart);
      map.on('styledata', maybeStart);
    } else {
      start();
    }

    return () => {
      // cleanup is handled when dependencies change
    };
  }, [
    geofenceEditSiteId,
    cleanupGeofenceEdit,
    makeDefaultRing,
    parseRingFromSite,
    setEditSourceData,
    emitGeofenceEditChange,
  ]);

  // A new explicit info click focuses once, after React opens the overlay.
  // Read current mode/editor state without subscribing to live site data.
  const modelFocusStateRef = useRef({ mapStyleMode, geofenceEditorEnabled });
  modelFocusStateRef.current = { mapStyleMode, geofenceEditorEnabled };
  useEffect(() => {
    if (!siteModelFocusRequest) return;
    const raf = requestAnimationFrame(() => {
      const map = mapRef.current;
      if (!map || modelFocusStateRef.current.geofenceEditorEnabled) return;
      const container = map.getContainer();
      const app = container.closest('.app');
      const sidebar = app?.querySelector<HTMLElement>('.secondary-sidebar:not(.collapsed)');
      // Use the final layout edge, not its animated translateX position.
      const coveredLeft = sidebar && !app?.classList.contains('fullscreen-mode')
        ? Math.max(0, sidebar.offsetLeft + sidebar.offsetWidth - container.getBoundingClientRect().left) : 0;
      focusSiteModel(map, siteModelFocusRequest.siteId, modelFocusStateRef.current.mapStyleMode === 'satellite', coveredLeft);
    });
    return () => cancelAnimationFrame(raf);
  }, [siteModelFocusRequest]);

  // Pan map to selected item
  useEffect(() => {
    if (!mapRef.current) return;
    if (!panToSelection) return;

    if (selectedVehicleId) {
      const vehicle = vehicles.find(v => v._id === selectedVehicleId);
      if (vehicle) {
        const n = normalizeLatLng(vehicle.latitude, vehicle.longitude);
        if (n.ok) mapRef.current.panTo([n.lng, n.lat]);
      }
    } else if (selectedSiteId) {
      const site = sites.find(s => s._id === selectedSiteId);
      if (site) {
        const n = normalizeLatLng(site.latitude, site.longitude);
        if (n.ok) mapRef.current.panTo([n.lng, n.lat]);
      }
    }
  }, [panToSelection, selectedVehicleId, selectedSiteId, vehicles, sites]);

  // Resize map when container size changes (e.g., sidebar collapse)
  useEffect(() => {
    if (!mapContainerRef.current || !mapRef.current) return;

    // Resize once per animation frame on container size changes.
    // (Avoid repeated resizes during sidebar animation, which can cause canvas flashing.)
    let rafId: number | null = null;
    const scheduleResize = () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const map = mapRef.current;
        if (!map) return;
        map.resize();
        map.triggerRepaint();
      });
    };

    const resizeObserver = new ResizeObserver(() => {
      scheduleResize();
    });

    resizeObserver.observe(mapContainerRef.current);

    return () => {
      resizeObserver.disconnect();
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    bodycamLayerRef.current?.update(bodycamPoints || [], bodycamTrail || [], selectedBodycamId || null);
  }, [bodycamPoints, bodycamTrail, selectedBodycamId]);
  useEffect(() => {
    if (!selectedBodycamId) { focusedBodycamRef.current = null; return; }
    const key = `${selectedBodycamId}:${bodycamFocusRequest || 0}`;
    if (focusedBodycamRef.current === key) return;
    const point = bodycamPoints?.find(p => p.id === selectedBodycamId);
    if (!point || !bodycamLayerRef.current) return;
    // Wait for the opened sidebar to lay out; retry when fresh GPS arrives.
    const frame = requestAnimationFrame(() => {
      bodycamLayerRef.current?.focus([point.longitude, point.latitude]);
      focusedBodycamRef.current = key;
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedBodycamId, bodycamFocusRequest, bodycamPoints]);

  return (
    <div className="map-container" style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
      {(hqModelError || cookModelError || fifteenthModelError || frontierModelError) && mapStyleMode === 'standard' && (
        <div role="status" className="map-model-error">{[hqModelError, cookModelError, fifteenthModelError, frontierModelError].filter(Boolean).join(' ')}</div>
      )}
    </div>
  );
};

export default React.memo(Map);
